import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { WatchIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { patchHabit } from '@/lib/goals-optimistic'

type Metric = 'steps' | 'sleepMinutes' | 'activeMinutes' | 'workoutMinutes'
type AutoCheck = { metric: Metric; min: number } | null

const OPTIONS: { metric: Metric; mins: number[] }[] = [
  { metric: 'steps', mins: [5000, 7500, 10000] },
  { metric: 'sleepMinutes', mins: [420, 480] },
  { metric: 'activeMinutes', mins: [20, 30, 45] },
  { metric: 'workoutMinutes', mins: [20, 30, 45] },
]

// Habit detail: tick this habit automatically from a connected wearable (Pro).
export function AutoCheckRow({ habitId }: { habitId: Id<'nodes'> }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isPro } = useIsPro()
  const habits = useQuery(api.nodes.getHabits)
  const conns = useQuery(api.fitness.myConnections)
  const setAutoCheck = useMutation(api.fitness.setAutoCheck).withOptimisticUpdate(
    (store, { habitId, autoCheck }) => patchHabit(store, habitId, { autoCheck }),
  )
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const current = (habits?.find((h) => h._id === habitId)?.autoCheck ?? null) as AutoCheck
  const fmt = new Intl.NumberFormat(i18n.language)
  const available = useQuery(api.fitness.available)
  if (!conns?.length && !available?.fitbit && !available?.strava) return null

  const label = (m: Metric, n: number) =>
    m === 'steps'
      ? t('fitness.steps', { n: fmt.format(n) })
      : m === 'sleepMinutes'
        ? t('fitness.sleep', { n: n / 60 })
        : m === 'activeMinutes'
          ? t('fitness.active', { n })
          : t('fitness.workout', { n })

  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border/60 px-3 py-2">
      <WatchIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground">{t('fitness.autoCheck')}</p>
        <p className="truncate text-[11px] text-muted-foreground">
          {conns?.length ? t('fitness.autoCheckDesc') : t('fitness.connectHint')}
        </p>
      </div>
      {conns?.length ? (
        <select
          aria-label={t('fitness.autoCheck')}
          value={current ? `${current.metric}:${current.min}` : ''}
          onChange={(e) => {
            if (!isPro) {
              setUpgradeOpen(true)
              return
            }
            const [metric, min] = e.target.value.split(':')
            void setAutoCheck({
              habitId,
              autoCheck: metric ? { metric: metric as Metric, min: Number(min) } : null,
            })
          }}
          className="max-w-[45%] cursor-pointer rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground"
        >
          <option value="">{t('fitness.off')}</option>
          {OPTIONS.flatMap(({ metric, mins }) =>
            mins.map((n) => (
              <option key={`${metric}:${n}`} value={`${metric}:${n}`}>
                {label(metric, n)}
              </option>
            )),
          )}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => navigate({ to: '/settings', hash: 'apps' })}
          className="shrink-0 cursor-pointer text-xs font-semibold text-primary-text hover:underline"
        >
          {t('fitness.connect')}
        </button>
      )}
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="pro" />
    </div>
  )
}
